import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { ROUTES } from '../../../routes/urls';
import {
  BackButton,
  Div,
  Icon,
  IconDiv,
  IconWrapper,
  LHS,
  Line,
  LogoutMenu,
  Menu,
  MenuSection,
  NavbarMain,
  ProfileMenu,
  RHS,
  Submenu,
  Title
} from './styles';
import { useSelector, useDispatch } from 'react-redux';
import ModalCustom from '../../models/modal';
import ConfirmationModal from '../../models/confirmationModal';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { saveProjectTemplateDetails, updateNotificationSettingDetails } from '../../../services/settingServices';
import { setThemeType, updateNotificationsSettingsDetails, updateThemeMode } from '../../../actions/settingActions';
import { RootReducerInterface } from '../../../interfaces/RootReducerInterface';
import { PROJECT_ANALYTICS } from '../../../global/analyticsConstants';
import { BottomNavigation } from '../../bottomNavigation';
import { useMobileDevice } from '../../../global/useMobile';
import { Dropdown, DropdownItem } from '../../Dropdown';
import { menuDisplayItems } from '../../../global/row';
import HelpSupportDropdown from '../navbarcomponent/helpSupportDropdown';
import { logout } from '../../../services/authServices';
import { COLOR_THEME, COLOR_THEME_TEXT, COLOR_THEME_TYPE } from '../../../global/constants';
import { getIsDarkMode } from '../../../helpers/common';
import { getUserPreferenceData, updateUserPreference } from '../../../helpers/firebaseHelper';
import ResponsiveHeader from '../../responsiveHeader/header';
import LayoutWithoutNavbar from '../../layoutWithoutNavbar';
import { rootStore } from '../../../mobx/rootStore';
import { convertProjectTemplateModelToInterface } from '../../../mobx/common';
import { MenuItem } from '../navbarcomponent';

const SettingNavbar: React.FC = () => {
  //State
  const [alertModelOpen, setAlertModalOpen] = useState(false);
  const [navigatePath, setNavigatePath] = useState('');
  const [updatedDataName, setUpdatedDataName] = useState('');
  const [openContactUsModal, setOpenContactUsModal] = useState(false);
  //use selector state variables
  const stateSelector = useSelector((state: RootReducerInterface) => state);
  const { settings: settingsSelector, workspace: workspaceSelector } = stateSelector || {};
  const { loading: settingLoading, notificationSettingDetails, themeType } = settingsSelector;
  const { workspace } = workspaceSelector;
  //Mobx store variable
  const { projectTemplateStore } = rootStore;
  const { currentProjectTemplate } = projectTemplateStore;
  //Other variable
  const isMobile = useMobileDevice();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const prevLocation = location.pathname;

  const onClickNavbarItem = useCallback(
    (path: string) => {
      history.push(path);
    },
    [history]
  );

  useEffect(() => {
    const unListen = history.listen((location) => {
      if (currentProjectTemplate?.updated && location.pathname !== prevLocation) {
        setUpdatedDataName('projectTemplateDetails');
        history.push(prevLocation);
        setAlertModalOpen(true);
        setNavigatePath(location.pathname);
      } else if (notificationSettingDetails.updated && location.pathname !== prevLocation) {
        setUpdatedDataName('notificationSettingDetails');
        history.push(prevLocation);
        setAlertModalOpen(true);
        setNavigatePath(location.pathname);
      }
    });
    return () => {
      unListen();
    };
  }, [history, notificationSettingDetails.updated, prevLocation, currentProjectTemplate?.updated]);

  //load them data
  const loadThemeData = useCallback(async () => {
    // Get theme mode from user preference
    const theme = await getUserPreferenceData('theme_mode');
    if (theme) {
      dispatch(setThemeType(theme));
    }
  }, [dispatch]);

  useEffect(() => {
    loadThemeData();
  }, [loadThemeData]);

  const closeAlertModal = useCallback(() => {
    if (updatedDataName === 'projectTemplateDetails') {
      currentProjectTemplate?.setUpdated(false);
    } else if (updatedDataName === 'notificationSettingDetails') {
      dispatch(updateNotificationsSettingsDetails({ propsName: 'updated', value: false }));
    }
    setAlertModalOpen(false);
    if (navigatePath) {
      history.push(navigatePath);
    }
  }, [currentProjectTemplate, dispatch, history, navigatePath, updatedDataName]);

  const onClickSave = useCallback(async () => {
    if (updatedDataName === 'projectTemplateDetails') {
      const cloneDetails = convertProjectTemplateModelToInterface(currentProjectTemplate!);
      trackAnalyticActivity(PROJECT_ANALYTICS.TEMPLATE_DATA_UPDATED);
      const result = await dispatch(saveProjectTemplateDetails(cloneDetails));
      currentProjectTemplate?.setUpdated(false);
      if (result) closeAlertModal();
    } else if (updatedDataName === 'notificationSettingDetails') {
      const cloneDetails = JSON.parse(JSON.stringify(notificationSettingDetails));
      delete cloneDetails?.updated;
      const result = await dispatch(updateNotificationSettingDetails(cloneDetails));
      if (result) closeAlertModal();
    }
  }, [closeAlertModal, dispatch, notificationSettingDetails, currentProjectTemplate, updatedDataName]);

  const onHelpSupport = useCallback(() => {
    setOpenContactUsModal(!openContactUsModal);
  }, [openContactUsModal]);

  const onClickLogout = useCallback(async () => {
    const result = await dispatch(logout());
    if (result) {
      history.push(ROUTES.LOGIN);
      window.location.reload();
    }
  }, [dispatch, history]);

  // Get theme mode
  const getThemeMode = useCallback((theme: string) => {
    if (theme === 'dark') {
      return COLOR_THEME.DARK;
    } else if (theme === 'light') {
      return COLOR_THEME.LIGHT;
    } else return COLOR_THEME.SYSTEM_DEFAULT;
  }, []);

  //handle theme change
  const onThemeChange = useCallback(
    async (theme: string) => {
      const themeMode = getThemeMode(theme);
      let resolvedThemeType;
      if (theme !== COLOR_THEME_TYPE.SYSTEM) {
        resolvedThemeType = theme;
      } else if (getIsDarkMode()) {
        resolvedThemeType = COLOR_THEME_TYPE.DARK;
      } else {
        resolvedThemeType = COLOR_THEME_TYPE.LIGHT;
      }
      // setIsActive(THEME_STATES[theme]);
      await updateUserPreference({ theme_mode: themeMode });
      dispatch(updateThemeMode({ propsName: 'theme', value: resolvedThemeType }));
      dispatch(setThemeType(themeMode));
      if ((window as any).electronApi) {
        (window as any).electronApi.changeTheme(theme);
      }
    },
    [dispatch, getThemeMode]
  );

  return (
    <>
      <LayoutWithoutNavbar>
        <NavbarMain className='setting-navbar'>
          {!isMobile ? (
            <BackButton onClick={() => onClickNavbarItem(ROUTES.INBOX)}>
              <SVGIcon
                name='setting-navbar-left-icon'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                className='fill-color'
              />
              <p>Back</p>
            </BackButton>
          ) : (
            <ResponsiveHeader Title='Settings' noMargin={true} />
          )}
          <MenuSection>
            {isMobile && (
              <>
                <Menu>
                  <Div>
                    <ProfileMenu>
                      <IconWrapper>
                        <Icon>
                          <SVGIcon
                            name='organization-icon'
                            width={18}
                            height={18}
                            viewBox='0 0 18 18'
                            className='svg-icon'
                          />
                        </Icon>
                        <LHS>
                          <p>Switch Organisation</p>
                        </LHS>
                      </IconWrapper>
                      <RHS
                        onClick={() => {
                          history.push(ROUTES.SELECT_WORKSPACE);
                        }}>
                        <p>{workspace.name}</p>
                        <IconDiv>
                          <SVGIcon
                            name='workspace-right-arrow-icon'
                            width='20'
                            height='20'
                            viewBox='0 0 18 18'
                            fill='var(--text-secondary)'
                          />
                        </IconDiv>
                      </RHS>
                    </ProfileMenu>
                    <ProfileMenu>
                      <IconWrapper>
                        <Icon>
                          <SVGIcon
                            name='display-icon'
                            width={18}
                            height={18}
                            viewBox='0 0 18 18'
                            className='svg-icon'
                          />
                        </Icon>
                        <LHS>
                          <p>Display</p>
                        </LHS>
                      </IconWrapper>
                      <RHS>
                        <Dropdown
                          trigger='click'
                          content={
                            <RHS>
                              <p>{COLOR_THEME_TEXT[themeType]}</p>
                              <IconDiv>
                                <SVGIcon
                                  name='dropdown-icon'
                                  width='20'
                                  height='20'
                                  viewBox='0 0 24 24'
                                  stroke=' var(--text-secondary)'
                                />
                              </IconDiv>
                            </RHS>
                          }
                          modalTitle='Display'>
                          {menuDisplayItems.map((item, index) => (
                            <DropdownItem
                              key={index}
                              label={item.label}
                              isSelected={item.label === COLOR_THEME_TEXT[themeType]}
                              onClick={() => onThemeChange(item.label.toLowerCase())}
                            />
                          ))}
                        </Dropdown>
                      </RHS>
                    </ProfileMenu>
                  </Div>
                </Menu>
                <Line />
              </>
            )}
            <Menu>
              <Title>
                <p>You</p>
              </Title>
              <Div>
                <MenuItem
                  path='/settings/personal'
                  isActive={location?.pathname?.startsWith('/settings/personal')}
                  label='My Profile'
                  iconName='my-profile-icon'
                />
                <MenuItem
                  path={ROUTES.EMAIL_SETTING}
                  isActive={location?.pathname?.startsWith(ROUTES.EMAIL_SETTING)}
                  label='Change your Email'
                  iconName='email-icon'
                />
                <MenuItem
                  path={ROUTES.PASSWORD_SETTING}
                  isActive={location?.pathname?.startsWith(ROUTES.PASSWORD_SETTING)}
                  label='Change Password'
                  iconName='password-icon'
                />
                <MenuItem
                  path={ROUTES.NOTIFICATION_SETTING}
                  isActive={location?.pathname?.startsWith(ROUTES.NOTIFICATION_SETTING)}
                  label='Notifications'
                  iconName='notification-icon'
                />
                <MenuItem
                  path={ROUTES.SECURITY_ACCESS_SETTING}
                  isActive={location?.pathname?.startsWith(ROUTES.SECURITY_ACCESS_SETTING)}
                  label='Security and access'
                  iconName='security-icon'
                />
                <MenuItem
                  path={ROUTES.REFEREARN_SETTING}
                  isActive={location?.pathname?.startsWith(ROUTES.REFEREARN_SETTING)}
                  label='Refer & Earn'
                  iconName='refer-earn-icon'
                />
                {isMobile && (
                  <Submenu isActive={false} onClick={onHelpSupport} className='menu-name'>
                    <HelpSupportDropdown
                      setOpenContactUsModal={() => setOpenContactUsModal(!openContactUsModal)}
                      isMobile={isMobile}
                      isSetting={true}
                    />
                  </Submenu>
                )}
              </Div>
            </Menu>
            {workspace?.id && workspace?.isAdmin && (
              <>
                <Line />
                <Menu>
                  <Title>
                    <p>Workspace</p>
                  </Title>
                  <Div>
                    <MenuItem
                      path={ROUTES.GENERAL_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.GENERAL_SETTING)}
                      label='Organisation Profile'
                      iconName='organization-icon'
                    />
                    <MenuItem
                      path={ROUTES.TEMPLATES_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.TEMPLATES_SETTING)}
                      label='Templates'
                      iconName='templates-icon'
                    />
                    <MenuItem
                      path={ROUTES.INVOICE_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.INVOICE_SETTING)}
                      label='Invoicing'
                      iconName='invoicing-icon'
                    />
                    <MenuItem
                      path={ROUTES.TEAM_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.TEAM_SETTING)}
                      label='Teams'
                      iconName='teams-icon'
                    />
                    <MenuItem
                      path={ROUTES.SUBSCRIPTION_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.SUBSCRIPTION_SETTING)}
                      label='Subscription'
                      iconName='subscription-icon'
                    />
                    <MenuItem
                      path={ROUTES.API_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.API_SETTING)}
                      label='API Keys'
                      iconName='api-icon'
                    />
                    <MenuItem
                      path={ROUTES.INTEGRATIONS_SETTING}
                      isActive={
                        location?.pathname?.startsWith(ROUTES.INTEGRATIONS_SETTING) ||
                        location?.pathname?.startsWith('/settings/integration/github')
                      }
                      label='Integrations'
                      iconName='integrations-icon'
                    />
                    <MenuItem
                      path={ROUTES.APPS_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.APPS_SETTING)}
                      label='Apps'
                      iconName='apps-icon'
                    />
                    <MenuItem
                      path={ROUTES.BRANDDOMAIN_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.BRANDDOMAIN_SETTING)}
                      label='Branding & Domain'
                      iconName='branding-domain-icon'
                    />
                    <MenuItem
                      path={ROUTES.USAGE_SETTING}
                      isActive={location?.pathname?.startsWith(ROUTES.USAGE_SETTING)}
                      label='Usage'
                      iconName='usage-icon'
                    />
                    {/* <Submenu
                      isActive={location?.pathname?.startsWith('/settings/ai-agents')}

                      onClick={() => onClickNavbarItem(ROUTES.AI_AGENTS_SETTING)}
                      className='menu-name'>
                  <Icon>
                      <SVGIcon name='ai-agents-icon' width={18} height={18} viewBox='0 0 18 18' className='svg-icon'/>
                      </Icon>
                      <span>AI Agents</span>
                    </Submenu> */}
                    {/* <Submenu
                      isActive={location?.pathname?.startsWith('/settings/payment')}
                      onClick={() => onClickNavbarItem(ROUTES.PAYMENT_SETTING)}
                      className='menu-name'>
                      <Icon>
                        <SVGIcon
                          name='online-payment-icon'
                          width={18}
                          height={18}
                          viewBox='0 0 18 18'
                          className='svg-icon'
                        />
                      </Icon>
                      <span>Online Payment</span>
                    </Submenu> */}
                  </Div>
                </Menu>
              </>
            )}
            {isMobile && (
              <>
                <Line />
                <Menu>
                  <Div className='bottom-div'>
                    <LogoutMenu onClick={onClickLogout}>
                      <Icon>
                        <SVGIcon
                          name='logout-icon'
                          width={20}
                          height={20}
                          viewBox='0 0 16 16'
                          className='logout-icon'
                        />
                      </Icon>
                      <LHS className='log-out'>
                        <p>Log out</p>
                      </LHS>
                    </LogoutMenu>
                  </Div>
                </Menu>
              </>
            )}
          </MenuSection>
        </NavbarMain>
        {/* bottom navigation bar */}
        <BottomNavigation />

        <ModalCustom open={alertModelOpen} onClose={closeAlertModal} width={334}>
          <ConfirmationModal
            loading={settingLoading}
            onOk={onClickSave}
            onClose={closeAlertModal}
            modalTitle='Save new changes?'
            description='Do you want to save new changes?'
          />
        </ModalCustom>
      </LayoutWithoutNavbar>
    </>
  );
};

export default SettingNavbar;
